import axios from "axios";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { LogIn, X } from "lucide-react";
import { Hero } from "../../components/ui/animated-hero";
import { LandingFooter } from "../../components/landing/landing-footer";

const WaitlistHero = lazy(() => import("../../components/ui/waitlist-hero"));

const TestimonialsSection = lazy(() =>
  import("../../components/ui/testimonial-v2").then((m) => ({
    default: m.TestimonialsSection,
  }))
);

const NAV_H = "h-16";

const Landing = () => {
  const navigate = useNavigate();
  const [loginOpen, setLoginOpen] = useState(false);
  const [loginInfo, setLoginInfo] = useState({
    email: "",
    password: "",
  });
  const [imageTwo, setImageTwo] = useState("");

  const abrirLogin = useCallback(() => {
    setLoginOpen(true);
    window.setTimeout(() => {
      document.getElementById("email-landing")?.focus();
    }, 150);
  }, []);

  const cerrarLogin = useCallback(() => {
    setLoginOpen(false);
  }, []);

  const handleGetImages = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/publicidad/landing`
      );

      const tempImageTwo = response.data.filter(
        (image: { placement: string }) => image.placement === "LandingMobile"
      );

      if (!tempImageTwo[0]?.link) {
        return;
      }

      try {
        const { data: s3Data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/s3/signed-url?key=${encodeURIComponent(tempImageTwo[0].link)}`,
          { headers: { "auth-token": sessionStorage.getItem("token") } }
        );
        setImageTwo(s3Data.url);
      } catch (err) {
        console.error(err);
      }
    } catch (error) {
      console.error(error);
    }
  }, []);

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setLoginInfo({ ...loginInfo, [name]: value });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    try {
      event.preventDefault();

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/auth/login`,
        loginInfo
      );

      sessionStorage.setItem("token", `${response.data.token}`);
      sessionStorage.setItem("rol", `${response.data.user.role}`);
      sessionStorage.setItem("user_id", `${response.data.user.usuario_id}`);
      sessionStorage.setItem("email", `${response.data.user.email}`);

      setLoginOpen(false);

      if (response.data.user.role === "Usuario") {
        navigate("/user");
      } else {
        navigate("/admin");
      }
    } catch {
      Swal.fire({
        title: "Error!",
        text: "Nos has podido ingresar, por favor verifica tus credenciales",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  useEffect(() => {
    handleGetImages();
  }, [handleGetImages]);

  useEffect(() => {
    if (!loginOpen) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        cerrarLogin();
      }
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [loginOpen, cerrarLogin]);

  return (
    <div id="page-landing" className="min-h-screen flex flex-col bg-background">
      <header
        className={`sticky top-0 z-[100] flex w-full items-center justify-between border-b border-border bg-white/95 px-4 py-0 shadow-sm backdrop-blur sm:px-6 ${NAV_H}`}
      >
        <a href="/" className="flex shrink-0 items-center">
          <img
            src={require("../../icons/Logo-Celmar.png")}
            alt="Celmar"
            className="h-9 w-auto sm:h-10"
          />
        </a>
        <button
          type="button"
          onClick={() => (loginOpen ? cerrarLogin() : abrirLogin())}
          className="inline-flex items-center gap-2 rounded-lg bg-[#205f43] px-4 py-2 text-sm font-medium text-white transition hover:bg-[#6CB7A2] sm:text-base"
        >
          <LogIn className="h-4 w-4" />
          Iniciar sesión
        </button>
      </header>

      {loginOpen && (
        <>
          <div
            className="fixed inset-0 z-[90] bg-black/40"
            onClick={cerrarLogin}
            role="presentation"
            aria-hidden
          />
          <div
            className="fixed left-3 right-3 top-[4.5rem] z-[110] max-h-[min(calc(100dvh-5.5rem),40rem)] overflow-y-auto sm:left-auto sm:right-6 sm:top-[4.75rem] sm:max-w-md"
            role="dialog"
            aria-modal="true"
            aria-labelledby="login-panel-title"
          >
            <div className="ml-auto w-full max-w-md origin-top rounded-xl border border-border bg-white p-5 shadow-2xl ring-1 ring-black/5">
              <div className="mb-4 flex items-start justify-between gap-2">
                <h2
                  id="login-panel-title"
                  className="text-xl font-bold text-foreground"
                >
                  Iniciar sesión
                </h2>
                <button
                  type="button"
                  onClick={cerrarLogin}
                  className="rounded-md p-1.5 text-muted-foreground transition hover:bg-muted"
                  aria-label="Cerrar"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              {imageTwo ? (
                <div className="mb-4">
                  <img
                    src={imageTwo}
                    alt=""
                    className="h-32 w-full rounded-md object-cover"
                  />
                </div>
              ) : null}
              <p className="mb-4 text-sm text-muted-foreground">
                Ingrese sus datos para abrir su cuenta
              </p>
              <form onSubmit={handleSubmit}>
                <div className="flex flex-col gap-4">
                  <div>
                    <label htmlFor="email-landing" className="text-sm">
                      Correo Electrónico
                    </label>
                    <input
                      type="text"
                      id="email-landing"
                      name="email"
                      value={loginInfo.email}
                      onChange={handleInputChange}
                      className="main-input"
                      required
                      placeholder="Correo Electrónico"
                      autoComplete="username"
                    />
                  </div>
                  <div>
                    <label htmlFor="password-landing" className="text-sm">
                      Contraseña
                    </label>
                    <input
                      type="password"
                      id="password-landing"
                      name="password"
                      value={loginInfo.password}
                      onChange={handleInputChange}
                      className="main-input"
                      required
                      placeholder="********"
                      autoComplete="current-password"
                    />
                  </div>
                  <a
                    href="/password-reset"
                    className="text-sm font-bold text-[#6CB7A2]"
                    onClick={cerrarLogin}
                  >
                    Olvidé mi contraseña
                  </a>
                  <button className="cefa-button" type="submit">
                    Ingresar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      <main className="relative w-full min-h-[calc(100dvh-4rem)] flex-1">
        <div id="inicio" className="relative z-10 w-full scroll-mt-20">
          <Hero className="w-full" />
        </div>
        <Suspense
          fallback={
            <div
              className="relative z-10 min-h-[28rem] w-full bg-zinc-50"
              aria-hidden
            />
          }
        >
          <div id="testimonios" className="relative z-10 scroll-mt-20">
            <TestimonialsSection />
          </div>
        </Suspense>
        <Suspense
          fallback={
            <div
              className="relative z-10 min-h-screen w-full bg-zinc-950"
              aria-hidden
            />
          }
        >
          <div id="waitlist-section" className="scroll-mt-20">
            <WaitlistHero />
          </div>
        </Suspense>
      </main>
      <LandingFooter onOpenLogin={abrirLogin} />
    </div>
  );
};

export default Landing;
